import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  IndividualsAPI,
  type AddressEditValues,
  type IndividualApiResponse,
  type IndividualEditValues
} from "../../api/individuals";
import { MattersAPI } from "../../api/matters";
import {
  PRONOUNS_OPTIONS,
  getLegacyOtherPronounsFallback,
  getPronounsSelectValue
} from "../../helpers/pronouns";
import { caseSettingsSchema } from "./caseSettingsSchema";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

export const TITLE_OPTIONS = ["Mr.", "Ms.", "Dr."];

const EMPTY_ADDRESS: AddressEditValues = {
  street: "",
  city: "",
  state: "",
  zipCode: "",
  country: ""
};

const EMPTY_VALUES: IndividualEditValues = {
  firstName: "",
  middleName: "",
  lastName: "",
  title: "",
  pronouns: "",
  dob: "",
  countryOfBirth: "",
  countryOfCitizenship: "",
  email: "",
  phone: "",
  visaType: "",
  isRfe: false,
  physicalAddress: EMPTY_ADDRESS,
  mailingAddress: EMPTY_ADDRESS,
  similarAddress: false,
  employerInfo: {
    legalBusinessName: "",
    dba: "",
    contactName: "",
    contactTitle: "",
    contactEmail: "",
    contactPhone: "",
    businessAddress: EMPTY_ADDRESS
  },
  employerPetitioner: false
};

const sameAddress = (a: AddressEditValues, b: AddressEditValues) =>
  a.street === b.street &&
  a.city === b.city &&
  a.state === b.state &&
  a.zipCode === b.zipCode &&
  a.country === b.country;

const normalizeLegacyPronounsForEditing = (
  pronouns: string,
  otherPronouns: string | undefined
): string => {
  const selectedPronouns = getPronounsSelectValue(pronouns);
  if (selectedPronouns !== "other") return pronouns;
  const legacyCustomPronouns = getLegacyOtherPronounsFallback(
    pronouns,
    otherPronouns
  );
  return legacyCustomPronouns || pronouns;
};

/**
 * Normalize an API record into form values: resolve legacy pronouns and make
 * the mailing address mirror the physical one when flagged as the same, so
 * the form isn't dirty the moment it loads.
 */
const toFormValues = (data: IndividualApiResponse): IndividualEditValues => {
  const normalized = IndividualsAPI.normalizeForEditForm(data);
  const rawOtherPronouns =
    typeof data?.otherPronouns === "string" ? data.otherPronouns : undefined;
  return {
    ...normalized,
    pronouns: normalizeLegacyPronounsForEditing(
      normalized.pronouns,
      rawOtherPronouns
    ),
    mailingAddress: normalized.similarAddress
      ? { ...normalized.physicalAddress }
      : normalized.mailingAddress
  };
};

/**
 * All state and side effects behind the Case Settings screen: load / save /
 * discard the individual's editable fields (a react-hook-form instance) and
 * the delete-case flow. The route supplies the individual id as `:id` (case
 * routes) or `:caseID` (`/cases/:caseID/edit`).
 */
export function useCaseSettingsForm() {
  const params = useParams<{ id?: string; caseID?: string }>();
  const caseID = params.id ?? params.caseID;
  const navigate = useNavigate();

  const form = useForm<IndividualEditValues>({
    resolver: zodResolver(caseSettingsSchema),
    defaultValues: EMPTY_VALUES
  });
  const { control, reset, setValue, getValues } = form;

  const [status, setStatus] = useState<LoadStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDbaEnabled, setIsDbaEnabled] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /** Replaces the persisted snapshot and re-derives the DBA switch. */
  const applyValues = useCallback(
    (next: IndividualEditValues) => {
      reset(next);
      setIsDbaEnabled(Boolean(next.employerInfo.dba.trim()));
    },
    [reset]
  );

  useEffect(() => {
    if (!caseID) return undefined;
    let cancelled = false;
    setStatus("loading");
    setErrorMessage("");

    IndividualsAPI.getIndividual(caseID)
      .then((data) => {
        if (cancelled) return;
        applyValues(toFormValues(data));
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage(
          err instanceof Error ? err.message : "Failed to load individual"
        );
      });

    return () => {
      cancelled = true;
    };
  }, [applyValues, caseID]);

  // Mailing mirrors physical while "same as physical address" is on.
  const similarAddress = useWatch({ control, name: "similarAddress" });
  const physicalAddress = useWatch({ control, name: "physicalAddress" });
  useEffect(() => {
    if (!similarAddress) return;
    if (sameAddress(getValues("mailingAddress"), physicalAddress)) return;
    setValue("mailingAddress", { ...physicalAddress }, { shouldDirty: true });
  }, [getValues, physicalAddress, setValue, similarAddress]);

  const setDbaEnabled = useCallback(
    (checked: boolean) => {
      setIsDbaEnabled(checked);
      if (!checked) {
        setValue("employerInfo.dba", "", { shouldDirty: true });
      }
    },
    [setValue]
  );

  const submit = useCallback<SubmitHandler<IndividualEditValues>>(
    async (values) => {
      if (!caseID) return;
      setIsSaving(true);
      try {
        const updated = await IndividualsAPI.updateIndividual(
          caseID,
          IndividualsAPI.toUpdatePayload(values)
        );
        applyValues(toFormValues(updated));
        setErrorMessage("");
        toast.success("Case settings saved");
      } catch (err: unknown) {
        setErrorMessage(
          err instanceof Error ? err.message : "Failed to save changes"
        );
      } finally {
        setIsSaving(false);
      }
    },
    [applyValues, caseID]
  );

  const handleSave = useCallback(async () => {
    await form.handleSubmit(submit)();
  }, [form, submit]);

  const handleDiscardChanges = useCallback(() => {
    reset();
    setIsDbaEnabled(Boolean(getValues("employerInfo.dba").trim()));
  }, [getValues, reset]);

  // Pronouns: the select shows a canonical choice; "other" reveals a free-text
  // input that edits the same underlying field.
  const pronouns = useWatch({ control, name: "pronouns" });
  const selectedPronounsValue = getPronounsSelectValue(pronouns);
  const customPronounsValue = getLegacyOtherPronounsFallback(
    pronouns,
    undefined
  );

  const [firstName, middleName, lastName] = useWatch({
    control,
    name: ["firstName", "middleName", "lastName"]
  });
  const expectedLastName = (lastName ?? "").trim();
  const individualDisplayName = [firstName, middleName, lastName]
    .filter((part) => (part ?? "").trim().length > 0)
    .join(" ")
    .trim();

  const openDeleteDialog = useCallback(() => setIsDeleteOpen(true), []);
  const closeDeleteDialog = useCallback(() => setIsDeleteOpen(false), []);

  const handleDeleteMatter = useCallback(async () => {
    if (isDeleting || !caseID || !expectedLastName) return;
    setIsDeleting(true);
    try {
      await MattersAPI.delete(caseID);
      toast.success("Case deleted", {
        description: "This case has been deleted."
      });
      closeDeleteDialog();
      navigate("/cases");
    } catch (e: unknown) {
      toast.error("Couldn't delete case", {
        description: e instanceof Error ? e.message : "Failed to delete case."
      });
    } finally {
      setIsDeleting(false);
    }
  }, [caseID, closeDeleteDialog, expectedLastName, isDeleting, navigate]);

  const options = useMemo(
    () => ({ titles: TITLE_OPTIONS, pronouns: PRONOUNS_OPTIONS }),
    []
  );

  return {
    caseId: caseID,
    form,
    status,
    isReady: status === "ready",
    errorMessage,
    isDirty: form.formState.isDirty,
    isSaving,
    isDbaEnabled,
    setDbaEnabled,
    selectedPronounsValue,
    customPronounsValue,
    options,
    submit,
    handleSave,
    handleDiscardChanges,
    // Delete flow
    isDeleteOpen,
    openDeleteDialog,
    closeDeleteDialog,
    isDeleting,
    expectedLastName,
    individualDisplayName,
    handleDeleteMatter
  };
}

export type CaseSettingsForm = ReturnType<typeof useCaseSettingsForm>;
